<template>
    <div class="music-main">
        <template v-if="ms.state.activeTab === 'radio'">
            <div class="music-toolbar">
                <div v-if="!searchOpen" class="music-toolbar-pills">
                    <button v-for="cat in radioCategories" :key="cat.key"
                            :class="['music-toolbar-pill', {'music-toolbar-pill--active': ms.state.radioCategory === cat.key}]"
                            @click="onCategoryClick(cat.key)">{{ cat.label }}</button>
                </div>
                <span v-if="!searchOpen" class="music-toolbar-spacer"></span>
                <div class="music-radio-search music-radio-search--inline">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input type="text"
                           :placeholder="$t('common.music.search')"
                           :value="radioQuery"
                           @input="radioQuery = $event.target.value; onRadioSearchDebounced()"
                           @keydown.enter="onRadioSearch">
                </div>
                <button v-if="!searchOpen" class="music-toolbar-btn music-radio-search-btn" @click="openRadioSearch">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                </button>
                <div v-if="searchOpen" class="music-radio-search music-radio-search--expanded">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input ref="radioSearchInput" type="text"
                           :placeholder="$t('common.music.search')"
                           v-model="radioQuery"
                           @keydown.enter="onRadioSearch"
                           @keydown.esc="closeRadioSearch"
                           @input="onRadioSearchDebounced">
                    <button class="music-search-close" @click="closeRadioSearch">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                </div>
            </div>

            <div v-if="ms.state.radioCategory === 'genres'" class="music-genre-bar">
                <button v-for="(tag, key) in ms.GENRE_TAGS" :key="key"
                        :class="['music-genre-pill', {'music-genre-pill--active': ms.state.radioGenre === key}]"
                        @click="ms.setRadioGenre(key)">{{ genreEmoji[key] }} {{ $t('common.music.genre' + capitalize(key)) }}</button>
            </div>

            <div v-if="ms.state.radioLoading && !stations.length" class="music-list">
                <div v-for="i in 6" :key="'skel-r-'+i" class="music-list-item music-skeleton-item">
                    <span class="music-list-favicon music-skeleton-box music-skeleton-box--radio"></span>
                    <div class="music-list-info">
                        <div class="music-skeleton-text" :style="{width: (40 + i * 9) + '%'}"></div>
                        <div class="music-skeleton-text music-skeleton-text--short"></div>
                    </div>
                </div>
            </div>

            <div v-else-if="stations.length" class="music-list">
                <div v-for="station in stations" :key="station.stationuuid"
                     :class="['music-list-item', {'music-list-item--active': isActiveStation(station)}]"
                     @click="$emit('play-station', station)">
                    <img v-if="station.favicon && !faviconErrors[station.stationuuid]" :src="station.favicon" class="music-list-favicon"
                         @error="faviconErrors[station.stationuuid] = true">
                    <span v-else class="music-list-favicon music-list-favicon--placeholder">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/></svg>
                    </span>
                    <div class="music-list-info">
                        <div class="music-list-name">{{ station.name }}</div>
                        <div class="music-list-meta">{{ stationMeta(station) }}</div>
                    </div>
                    <span v-if="isActiveStation(station) && ms.state.playing" class="music-list-eq">
                        <span class="music-list-eq-bar"></span>
                        <span class="music-list-eq-bar"></span>
                        <span class="music-list-eq-bar"></span>
                    </span>
                    <button :class="['music-list-fav', {'music-list-fav--active': ms.isFavorite(station.stationuuid)}]"
                            @click.stop="toggleFav(station)">
                        <svg width="14" height="14" viewBox="0 0 24 24" :fill="ms.isFavorite(station.stationuuid) ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                        </svg>
                    </button>
                </div>
            </div>

            <div v-else-if="!ms.state.radioLoading" class="music-empty">
                <div class="music-empty-icon">
                    <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <circle cx="12" cy="12" r="2"/><path d="M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49"/>
                    </svg>
                </div>
                <span class="music-empty-title">
                    {{ ms.state.radioCategory === 'favorites' ? $t('common.music.noFavorites') : $t('common.music.noResults') }}
                </span>
                <span class="music-empty-hint">
                    {{ ms.state.radioCategory === 'favorites' ? $t('common.music.noFavoritesHint') : $t('common.music.noResultsHint') }}
                </span>
            </div>
        </template>

        <template v-else>
            <div class="music-toolbar">
                <div v-if="!searchActive" class="music-toolbar-source">
                    <svg v-if="isPlaylistView" class="music-toolbar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15V6"/><path d="M18.5 18a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"/><path d="M12 12H3"/><path d="M16 6H3"/><path d="M12 18H3"/></svg>
                    <svg v-else class="music-toolbar-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                    <div class="music-toolbar-source-info">
                        <span class="music-toolbar-title">{{ toolbarTitle }}</span>
                        <span v-if="trackCount != null" class="music-toolbar-subtitle">{{ trackCount }} {{ $t('common.music.trackUnit') }}</span>
                    </div>
                </div>
                <span v-if="!searchActive" class="music-toolbar-spacer"></span>
                <div class="music-toolbar-search music-toolbar-search--inline">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                    <input type="text"
                           :placeholder="$t('common.music.searchTracks')"
                           :value="ms.state.librarySearchQuery"
                           @input="onTrackSearch($event)">
                </div>
                <button v-if="!searchActive" class="music-toolbar-btn music-toolbar-search-btn" @click="openSearch">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                </button>
                <div v-if="searchActive" class="music-toolbar-search music-toolbar-search--expanded">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                    <input ref="trackSearchInput" type="text"
                           :placeholder="$t('common.music.searchTracks')"
                           :value="ms.state.librarySearchQuery"
                           @input="onTrackSearch($event)"
                           @keydown.esc="closeSearch">
                    <button class="music-toolbar-search-close" @click="closeSearch">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                </div>
                <div class="music-toolbar-divider"></div>
                <button :class="['music-toolbar-btn', {'music-toolbar-btn--active': ms.state.selectMode}]"
                        @click="ms.toggleSelectMode()">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                </button>
                <div class="music-toolbar-divider"></div>
                <button class="music-upload-btn" @click="$emit('upload')">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    <span>{{ $t('common.music.upload') }}</span>
                </button>
            </div>

            <div v-if="ms.state.selectMode" class="music-selection-bar">
                <button class="music-selection-chip" @click="ms.clearTrackSelection()">
                    <strong>{{ ms.state.selectedTrackIds.length }}</strong> {{ $t('common.music.selected') }}
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
                <button v-if="ms.state.selectedTrackIds.length < ms.state.libraryTracks.length"
                        class="music-selection-btn" @click="ms.selectAllTracks()">
                    {{ $t('common.music.selectAll') }}
                </button>
                <span class="music-toolbar-spacer"></span>
                <div v-if="assignablePlaylists.length" class="music-selection-move-wrap">
                    <button class="music-selection-btn" @click="selectionDropdownOpen = !selectionDropdownOpen">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15V6"/><path d="M18.5 18a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"/><path d="M12 12H3"/><path d="M16 6H3"/><path d="M12 18H3"/></svg>
                        <span class="music-selection-btn-label">{{ $t('common.music.addToPlaylist') }}</span>
                    </button>
                    <div v-if="selectionDropdownOpen" class="music-selection-dropdown" @click.stop>
                        <button v-for="pl in assignablePlaylists" :key="pl.id"
                                class="music-playlist-dropdown-item"
                                @click="onBatchAddToPlaylist(pl)">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15V6"/><path d="M18.5 18a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"/><path d="M12 12H3"/><path d="M16 6H3"/><path d="M12 18H3"/></svg>
                            {{ pl.name }}
                        </button>
                    </div>
                </div>
                <button v-if="canRemoveFromPlaylist" class="music-selection-btn music-selection-btn--danger"
                        @click="onBatchRemoveFromPlaylist">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    <span class="music-selection-btn-label">{{ $t('common.music.deleteTrack') }}</span>
                </button>
            </div>

            <div v-if="ms.state.libraryLoading && !ms.state.libraryTracks.length" class="music-list">
                <div v-for="i in 6" :key="'skel-t-'+i" class="music-list-item music-skeleton-item">
                    <div class="music-list-cover music-skeleton-box music-skeleton-box--cover"></div>
                    <div class="music-list-info">
                        <div class="music-skeleton-text" :style="{width: (40 + i * 9) + '%'}"></div>
                        <div class="music-skeleton-text music-skeleton-text--short"></div>
                    </div>
                    <span class="music-skeleton-text music-skeleton-text--duration"></span>
                </div>
            </div>

            <div v-else-if="ms.state.libraryTracks.length" ref="musicList" class="music-list">
                <div v-for="(track, idx) in ms.state.libraryTracks" :key="track.id"
                     :class="['music-list-item', {
                         'music-list-item--active': isActiveTrack(track),
                         'music-list-item--dragging': draggingTrackId === track.id,
                         'music-list-item--drag-over': dragOverIdx === idx
                     }]"
                     :data-track-id="track.id"
                     draggable="true"
                     @click="onTrackClick(track)"
                     @contextmenu.prevent="openContextMenu($event, track)"
                     @touchstart.passive="onTouchStart($event, track)"
                     @touchmove.passive="onTouchMove"
                     @touchend="onTouchEnd($event, track)"
                     @dragstart="onTrackDragStart($event, track, idx)"
                     @dragend="onTrackDragEnd"
                     @dragover.prevent="onTrackDragOver($event, idx)"
                     @drop="onTrackDrop($event, idx)">
                    <span v-if="ms.state.selectMode"
                          :class="['music-list-select', {'music-list-select--checked': isSelected(track.id)}]"
                          @click.stop="ms.toggleTrackSelect(track.id)"></span>
                    <span v-if="isLibraryTab && !ms.state.selectMode" class="music-list-grip"
                          @touchstart.stop.prevent="onGripTouchStart($event, track, idx)">
                        <svg width="8" height="14" viewBox="0 0 8 14" fill="currentColor"><circle cx="2" cy="2" r="1.2"/><circle cx="6" cy="2" r="1.2"/><circle cx="2" cy="7" r="1.2"/><circle cx="6" cy="7" r="1.2"/><circle cx="2" cy="12" r="1.2"/><circle cx="6" cy="12" r="1.2"/></svg>
                    </span>
                    <div class="music-list-cover">
                        <img v-if="track.hasCover && track.thumb" :src="track.thumb.small" class="music-list-cover-img" @error="$event.target.style.display='none'">
                        <div v-else class="music-list-cover-placeholder">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                        </div>
                    </div>
                    <div class="music-list-info">
                        <div class="music-list-name">{{ track.title }}</div>
                        <div class="music-list-meta">{{ trackMeta(track) }}</div>
                    </div>
                    <span v-if="isActiveTrack(track) && ms.state.playing" class="music-list-eq music-list-eq--inline">
                        <span class="music-list-eq-bar"></span>
                        <span class="music-list-eq-bar"></span>
                        <span class="music-list-eq-bar"></span>
                    </span>
                    <span class="music-list-duration">{{ formatDuration(track.duration) }}</span>
                    <button :class="['music-list-fav', {'music-list-fav--active': ms.isFavoriteTrack(track.id)}]"
                            @click.stop="toggleTrackFav(track)">
                        <svg width="14" height="14" viewBox="0 0 24 24" :fill="ms.isFavoriteTrack(track.id) ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                        </svg>
                    </button>
                </div>
                <div ref="infiniteSentinel" class="music-infinite-sentinel"></div>
            </div>

            <div v-else-if="!ms.state.libraryLoading" class="music-empty">
                <div class="music-empty-icon">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
                    </svg>
                </div>
                <span class="music-empty-title">{{ $t('common.music.noTracks') }}</span>
                <span class="music-empty-hint">{{ $t('common.music.noTracksHint') }}</span>
            </div>
        </template>

        <teleport to="body">
            <template v-if="ctxMenu">
                <div class="music-context-backdrop" @click="closeContextMenu" @contextmenu.prevent="closeContextMenu"></div>
                <div class="music-context-menu" :style="ctxMenuStyle">
                    <template v-if="assignablePlaylists.length">
                        <div class="music-context-header">{{ $t('common.music.addToPlaylist') }}</div>
                        <button v-for="pl in assignablePlaylists" :key="pl.id"
                                class="music-context-item"
                                @click="onCtxAddToPlaylist(pl)">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15V6"/><path d="M18.5 18a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"/><path d="M12 12H3"/><path d="M16 6H3"/><path d="M12 18H3"/></svg>
                            {{ pl.name }}
                        </button>
                        <div class="music-context-divider"></div>
                    </template>
                    <button :class="['music-context-item', {'music-context-item--active': ms.isFavoriteTrack(ctxMenu.track.id)}]"
                            @click="onCtxToggleFav">
                        <svg width="12" height="12" viewBox="0 0 24 24" :fill="ms.isFavoriteTrack(ctxMenu.track.id) ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                        </svg>
                        {{ ms.isFavoriteTrack(ctxMenu.track.id) ? $t('common.music.removeFavorite') : $t('common.music.addFavorite') }}
                    </button>
                    <template v-if="canRemoveFromPlaylist">
                        <div class="music-context-divider"></div>
                        <button class="music-context-item music-context-item--danger"
                                @click="onCtxRemoveFromPlaylist">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            {{ $t('common.music.removeFromPlaylist') }}
                        </button>
                    </template>
                </div>
            </template>
        </teleport>
    </div>
</template>

<script>
import musicService from './musicService'

export default {
    name: 'MusicContent',
    emits: ['play-station', 'upload'],
    data() {
        return {
            searchOpen: false,
            radioQuery: '',
            searchActive: false,
            draggingTrackId: null,
            dragOverIdx: null,
            ctxMenu: null,
            selectionDropdownOpen: false,
            faviconErrors: {},
            genreEmoji: { pop: '🎤', rock: '🎸', turkish: '🇹🇷', jazz: '🎷', classical: '🎻', folk: '🪕', arabesk: '🎹', news: '📰' },
        }
    },
    computed: {
        ms() { return musicService },
        stations() {
            if (musicService.state.radioCategory === 'favorites') return musicService.state.favorites
            return musicService.state.stations
        },
        radioCategories() {
            return [
                { key: 'favorites', label: '❤️ ' + this.$t('common.music.favorites') },
                { key: 'popular', label: '🔥 ' + this.$t('common.music.popular') },
                { key: 'genres', label: '🎭 ' + this.$t('common.music.genres') },
            ]
        },
        trackCount() {
            const pl = musicService.state.playlists.find(p =>
                p.id === musicService.state.activePlaylistId || (!musicService.state.activePlaylistId && p.isLibrary)
            )
            return pl ? pl.trackCount : null
        },
        assignablePlaylists() {
            const currentId = musicService.state.activePlaylistId
            return musicService.state.playlists.filter(pl => {
                if (pl.isLibrary) return false
                if (pl.id === currentId) return false
                return true
            })
        },
        canRemoveFromPlaylist() {
            const pl = musicService.state.playlists.find(p => p.id === musicService.state.activePlaylistId)
            return pl && !pl.isLibrary
        },
        isPlaylistView() {
            return musicService.state.activeTab === 'library' && musicService.state.activePlaylistId
        },
        isLibraryTab() {
            return musicService.state.activeTab === 'library'
        },
        currentPlaylistId() {
            return musicService.state.activePlaylistId || (musicService.libraryPlaylist.value?.id ?? null)
        },
        toolbarTitle() {
            if (musicService.state.activePlaylistId) {
                const pl = musicService.state.playlists.find(p => p.id === musicService.state.activePlaylistId)
                return pl ? pl.name : ''
            }
            return this.$t('common.music.library')
        },
        ctxMenuStyle() {
            if (!this.ctxMenu) return {}
            return {
                left: this.ctxMenu.x + 'px',
                top: this.ctxMenu.y + 'px',
            }
        },
    },
    created() {
        this._searchTimer = null
        this._radioSearchTimer = null
        this._lpTimer = null
        this._lpReady = false
        this._touchStartX = 0
        this._touchStartY = 0
    },
    methods: {
        capitalize(s) { return s.charAt(0).toUpperCase() + s.slice(1) },
        stationMeta(station) {
            const parts = []
            if (station.tags) {
                const first = station.tags.split(',')[0].trim()
                if (first) parts.push(first)
            }
            if (station.bitrate) parts.push(station.bitrate + ' kbps')
            if (station.country) parts.push(station.country)
            return parts.join(' · ')
        },
        trackMeta(track) {
            const parts = []
            if (track.artist) parts.push(track.artist)
            if (track.format) parts.push(track.format.toUpperCase())
            if (track.sizeText) parts.push(track.sizeText)
            return parts.join(' · ')
        },
        formatDuration(seconds) {
            if (!seconds) return '--:--'
            const m = Math.floor(seconds / 60)
            const s = Math.floor(seconds % 60)
            return m + ':' + String(s).padStart(2, '0')
        },
        isActiveStation(station) {
            return musicService.state.currentStation?.stationuuid === station.stationuuid
        },
        isActiveTrack(track) {
            return musicService.state.currentTrack?.id === track.id
        },
        isSelected(id) {
            return musicService.state.selectedTrackIds.includes(id)
        },
        toggleFav(station) {
            if (musicService.isFavorite(station.stationuuid)) musicService.removeFavorite(station.stationuuid)
            else musicService.addFavorite(station)
        },
        toggleTrackFav(track) {
            if (musicService.isFavoriteTrack(track.id)) musicService.removeFavoriteTrack(track.id)
            else musicService.addFavoriteTrack(track.id)
        },
        onTrackClick(track) {
            if (musicService.state.selectMode) {
                musicService.toggleTrackSelect(track.id)
            } else {
                musicService.playTrack(track)
            }
        },
        onCategoryClick(key) {
            musicService.setRadioCategory(key)
        },
        openRadioSearch() {
            this.searchOpen = true
            this.radioQuery = ''
            this.$nextTick(() => this.$refs.radioSearchInput?.focus())
        },
        closeRadioSearch() {
            this.searchOpen = false
            this.radioQuery = ''
        },
        onRadioSearch() {
            clearTimeout(this._radioSearchTimer)
            if (this.radioQuery.trim()) {
                musicService.searchStations(this.radioQuery.trim())
            }
        },
        onRadioSearchDebounced() {
            clearTimeout(this._radioSearchTimer)
            this._radioSearchTimer = setTimeout(() => {
                if (this.radioQuery.trim().length >= 2) {
                    musicService.state.radioCategory = 'search'
                    musicService.searchStations(this.radioQuery.trim())
                }
            }, 400)
        },
        openSearch() {
            this.searchActive = true
            this.$nextTick(() => this.$refs.trackSearchInput?.focus())
        },
        closeSearch() {
            this.searchActive = false
            if (musicService.state.librarySearchQuery) {
                musicService.searchLibrary('')
            }
        },
        onTrackSearch(e) {
            clearTimeout(this._searchTimer)
            const q = e.target.value
            this._searchTimer = setTimeout(() => {
                musicService.searchLibrary(q)
            }, 400)
        },
        openContextMenu(e, track) {
            const x = Math.min(e.clientX, window.innerWidth - 200)
            const y = Math.min(e.clientY, window.innerHeight - 260)
            this.ctxMenu = { track, x, y }
        },
        closeContextMenu() {
            this.ctxMenu = null
        },
        async onCtxAddToPlaylist(pl) {
            const trackId = this.ctxMenu.track.id
            this.ctxMenu = null
            await musicService.addTrackToPlaylist(trackId, pl.id)
        },
        onCtxToggleFav() {
            this.toggleTrackFav(this.ctxMenu.track)
            this.ctxMenu = null
        },
        async onCtxRemoveFromPlaylist() {
            const trackId = this.ctxMenu.track.id
            this.ctxMenu = null
            await musicService.removeTrackFromPlaylist(trackId, musicService.state.activePlaylistId)
        },
        async onBatchAddToPlaylist(pl) {
            this.selectionDropdownOpen = false
            const ids = [...musicService.state.selectedTrackIds]
            await musicService.addTracksToPlaylist(ids, pl.id)
        },
        async onBatchRemoveFromPlaylist() {
            const ids = [...musicService.state.selectedTrackIds]
            await musicService.removeTracksFromPlaylist(ids, musicService.state.activePlaylistId)
        },
        onTouchStart(e, track) {
            if (musicService.state.selectMode) return
            const touch = e.touches[0]
            this._touchStartX = touch.clientX
            this._touchStartY = touch.clientY
            this._lpReady = false
            this._lpTrack = track
            clearTimeout(this._lpTimer)
            this._lpTimer = setTimeout(() => {
                this._lpReady = true
                try { navigator.vibrate(15) } catch { /* empty */ }
            }, 500)
        },
        onTouchMove(e) {
            if (!this._lpTimer && !this._lpReady) return
            const touch = e.touches[0]
            const dx = Math.abs(touch.clientX - this._touchStartX)
            const dy = Math.abs(touch.clientY - this._touchStartY)
            if (dx > 10 || dy > 10) {
                clearTimeout(this._lpTimer)
                this._lpTimer = null
            }
        },
        onTouchEnd(e, track) {
            clearTimeout(this._lpTimer)
            this._lpTimer = null
            if (this._lpReady) {
                const touch = e.changedTouches[0]
                const dx = Math.abs(touch.clientX - this._touchStartX)
                const dy = Math.abs(touch.clientY - this._touchStartY)
                if (dx < 40 && dy < 40) {
                    e.preventDefault()
                    const x = Math.min(touch.clientX, window.innerWidth - 200)
                    const y = Math.min(touch.clientY, window.innerHeight - 260)
                    this.ctxMenu = { track, x, y }
                }
                this._lpReady = false
            }
        },
        onGripTouchStart(e, track, idx) {
            const touch = e.touches[0]
            const listItem = e.currentTarget.closest('.music-list-item')
            const list = this.$refs.musicList
            if (!listItem || !list) return
            const rect = listItem.getBoundingClientRect()
            const ghost = listItem.cloneNode(true)
            ghost.style.cssText = 'position:fixed;z-index:200000;pointer-events:none;opacity:0.85;'
                + 'width:' + rect.width + 'px;height:' + rect.height + 'px;left:' + rect.left + 'px;'
                + 'background:var(--music-item-hover,#f0f0f0);border-radius:8px;'
                + 'box-shadow:0 4px 16px rgba(0,0,0,0.18);transition:none;'
            ghost.style.top = (touch.clientY - (touch.clientY - rect.top)) + 'px'
            document.body.appendChild(ghost)
            this._gripDrag = {
                trackId: track.id,
                fromIdx: idx,
                ghost,
                list,
                offsetY: touch.clientY - rect.top,
                itemHeight: rect.height,
                touchY: touch.clientY,
            }
            this.draggingTrackId = track.id
            try { navigator.vibrate(10) } catch { /* empty */ }
            this._gripMove = (ev) => this._onGripTouchMove(ev)
            this._gripEnd = () => this._onGripTouchEnd()
            document.addEventListener('touchmove', this._gripMove, { passive: false })
            document.addEventListener('touchend', this._gripEnd)
            document.addEventListener('touchcancel', this._gripEnd)
            this._autoScrollRAF = requestAnimationFrame(() => this._autoScrollTick())
        },
        _onGripTouchMove(e) {
            e.preventDefault()
            const g = this._gripDrag
            if (!g) return
            const touch = e.touches[0]
            g.touchY = touch.clientY
            g.ghost.style.top = (touch.clientY - g.offsetY) + 'px'
            this._recalcDragTarget()
        },
        _recalcDragTarget() {
            const g = this._gripDrag
            if (!g) return
            const listRect = g.list.getBoundingClientRect()
            const relativeY = g.touchY - listRect.top + g.list.scrollTop
            const targetIdx = Math.max(0, Math.min(
                Math.floor(relativeY / g.itemHeight),
                musicService.state.libraryTracks.length - 1
            ))
            this.dragOverIdx = targetIdx !== g.fromIdx ? targetIdx : null
        },
        _autoScrollTick() {
            const g = this._gripDrag
            if (!g) return
            const listRect = g.list.getBoundingClientRect()
            const edge = 50
            const speed = 6
            if (g.touchY < listRect.top + edge && g.list.scrollTop > 0) {
                g.list.scrollTop -= speed
                this._recalcDragTarget()
            } else if (g.touchY > listRect.bottom - edge) {
                g.list.scrollTop += speed
                this._recalcDragTarget()
            }
            this._autoScrollRAF = requestAnimationFrame(() => this._autoScrollTick())
        },
        _onGripTouchEnd() {
            cancelAnimationFrame(this._autoScrollRAF)
            document.removeEventListener('touchmove', this._gripMove)
            document.removeEventListener('touchend', this._gripEnd)
            document.removeEventListener('touchcancel', this._gripEnd)
            const g = this._gripDrag
            if (g) {
                g.ghost.remove()
                if (this.dragOverIdx != null) {
                    const tracks = musicService.state.libraryTracks
                    const fromIdx = tracks.findIndex(t => t.id === g.trackId)
                    if (fromIdx !== -1 && fromIdx !== this.dragOverIdx) {
                        const [item] = tracks.splice(fromIdx, 1)
                        const insertIdx = fromIdx < this.dragOverIdx ? this.dragOverIdx - 1 : this.dragOverIdx
                        tracks.splice(insertIdx, 0, item)
                        musicService.saveTrackOrder(musicService.state.activePlaylistId)
                    }
                }
            }
            this.draggingTrackId = null
            this.dragOverIdx = null
            this._gripDrag = null
        },
        onTrackDragStart(e, track) {
            e.dataTransfer.setData('music/track-id', String(track.id))
            e.dataTransfer.effectAllowed = 'copyMove'
            this.draggingTrackId = track.id
            const el = e.currentTarget
            const rect = el.getBoundingClientRect()
            const ghost = el.cloneNode(true)
            ghost.style.cssText = 'position:fixed;top:-9999px;left:-9999px;'
                + 'width:' + rect.width + 'px;height:' + rect.height + 'px;'
                + 'opacity:0.85;background:var(--music-item-hover,#f0f0f0);'
                + 'border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,0.18);'
            document.body.appendChild(ghost)
            e.dataTransfer.setDragImage(ghost, e.clientX - rect.left, e.clientY - rect.top)
            requestAnimationFrame(() => ghost.remove())
        },
        onTrackDragEnd() {
            this.draggingTrackId = null
            this.dragOverIdx = null
        },
        onTrackDragOver(e, idx) {
            if (!this.isLibraryTab || this.draggingTrackId == null) return
            e.dataTransfer.dropEffect = 'move'
            this.dragOverIdx = idx
        },
        onTrackDrop(e, targetIdx) {
            if (!this.isLibraryTab || this.draggingTrackId == null) return
            this.dragOverIdx = null
            const tracks = musicService.state.libraryTracks
            const fromIdx = tracks.findIndex(t => t.id === this.draggingTrackId)
            if (fromIdx === -1 || fromIdx === targetIdx) return
            const [item] = tracks.splice(fromIdx, 1)
            const insertIdx = fromIdx < targetIdx ? targetIdx - 1 : targetIdx
            tracks.splice(insertIdx, 0, item)
            musicService.saveTrackOrder(musicService.state.activePlaylistId)
        },
        onDocClick(e) {
            if (this.selectionDropdownOpen && !e.target.closest('.music-selection-move-wrap')) {
                this.selectionDropdownOpen = false
            }
        },
        _setupObservers() {
            this._resizeObs = new ResizeObserver(() => this._calcPageLimit())
            this._connectList()
        },
        _connectList() {
            const list = this.$refs.musicList
            const sentinel = this.$refs.infiniteSentinel
            if (!list || !sentinel) return
            if (this._observedList === list) {
                this._infiniteObs?.unobserve(sentinel)
                this._infiniteObs?.observe(sentinel)
                return
            }
            this._infiniteObs?.disconnect()
            if (this._observedList) this._resizeObs?.unobserve(this._observedList)
            this._infiniteObs = new IntersectionObserver((entries) => {
                if (entries[0]?.isIntersecting) musicService.loadMoreTracks()
            }, { root: list, threshold: 0 })
            this._infiniteObs.observe(sentinel)
            this._resizeObs.observe(list)
            this._observedList = list
            this._calcPageLimit()
        },
        _calcPageLimit() {
            const el = this.$refs.musicList
            if (el) musicService.updatePageLimit(el.clientHeight)
        },
    },
    mounted() {
        document.addEventListener('mousedown', this.onDocClick)
        this._setupObservers()
        this._unwatchReobserve = this.$watch(
            () => musicService.state.libraryTracks.length,
            () => this.$nextTick(() => this._connectList())
        )
    },
    beforeUnmount() {
        clearTimeout(this._searchTimer)
        clearTimeout(this._radioSearchTimer)
        clearTimeout(this._lpTimer)
        cancelAnimationFrame(this._autoScrollRAF)
        document.removeEventListener('touchmove', this._gripMove)
        document.removeEventListener('touchend', this._gripEnd)
        document.removeEventListener('touchcancel', this._gripEnd)
        document.removeEventListener('mousedown', this.onDocClick)
        this._infiniteObs?.disconnect()
        this._resizeObs?.disconnect()
        this._unwatchReobserve?.()
        this._observedList = null
    },
}
</script>
